import React from 'react';
import { Bus } from '../types';
import { Bus as BusIcon, Clock, Users, IndianRupee, RefreshCw } from 'lucide-react';

interface BusResultsProps {
  buses: Bus[];
  lastUpdated: string | null;
  onRefresh: () => void;
  isLoading: boolean;
}

export const BusResults: React.FC<BusResultsProps> = ({
  buses,
  lastUpdated,
  onRefresh,
  isLoading,
}) => {
  const formatTime = (timeString: string) => {
    return timeString || 'N/A';
  };

  const formatSeats = (seats: string | string[]) => {
    if (Array.isArray(seats)) {
      return seats.length > 0 ? seats.slice(0, 5).join(', ') + (seats.length > 5 ? '...' : '') : 'None';
    }
    return seats === 'Full' ? 'Full' : seats || 'N/A';
  };

  const formatLastUpdated = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <BusIcon className="w-5 h-5" />
            Available Buses ({buses.length})
          </h3>
          <button
            onClick={onRefresh}
            disabled={isLoading}
            className={`p-2 rounded-lg transition-all duration-200 ${
              isLoading 
                ? 'bg-white/20 cursor-not-allowed' 
                : 'bg-white/20 hover:bg-white/30'
            } text-white`}
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        {lastUpdated && (
          <p className="text-purple-100 text-sm mt-1">
            Last updated: {formatLastUpdated(lastUpdated)}
          </p>
        )}
      </div>

      <div className="p-6">
        {buses.length === 0 ? (
          <div className="text-center py-12">
            <BusIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">No buses found</h4>
            <p className="text-gray-500">
              No buses match your criteria yet. The monitor will continue checking for updates.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {buses.map((bus, index) => (
              <div 
                key={bus.tripCode || index}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all duration-200 hover:border-blue-300"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  {/* Bus Info */}
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="bg-blue-100 p-2 rounded-lg">
                        <BusIcon className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{bus.tripCode}</h4>
                        <p className="text-sm text-gray-600">{bus.busType}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-green-500" />
                        <span className="text-gray-600">Departure:</span>
                        <span className="font-medium">{formatTime(bus.departure_time)}</span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-red-500" />
                        <span className="text-gray-600">Arrival:</span>
                        <span className="font-medium">{formatTime(bus.arrival_time)}</span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <IndianRupee className="w-4 h-4 text-orange-500" />
                        <span className="text-gray-600">Fare:</span>
                        <span className="font-medium">₹{bus.price}</span>
                      </div>

                      {/* <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-purple-500" />
                        <span className="text-gray-600">Seat Numbers:</span>
                        <span className="font-medium text-xs">
                          {bus.available_seats_numbers ? 
                            (Array.isArray(bus.available_seats_numbers) ? 
                              bus.available_seats_numbers.slice(0, 8).join(', ') + 
                              (bus.available_seats_numbers.length > 8 ? '...' : '') : 
                              bus.available_seats_numbers
                            ) : 'N/A'
                          }
                        </span>
                      </div> */}
                    </div>
                  </div>

                  {/* Available Seats */}
                  <div className="md:text-right">
                    <div className="flex items-center gap-2 justify-end mb-2">
                      <Users className="w-4 h-4 text-purple-500" />
                      <span className="text-sm text-gray-600">Available Seats:</span>
                    </div>
                    <div className="bg-gradient-to-r from-green-50 to-blue-50 px-3 py-2 rounded-lg border">
                      <p className="font-medium text-gray-900">
                        {formatSeats(bus.available_seats)}
                      </p>
                      {Array.isArray(bus.available_seats) && bus.available_seats.length > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          Total: {bus.available_seats.length} seats
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Seat Numbers Detail (for arrays) */}
                {Array.isArray(bus.available_seats) && bus.available_seats.length > 5 && (
                  <div className="mt-4 pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-600 mb-2">All available seats:</p>
                    <div className="flex flex-wrap gap-1">
                      {bus.available_seats.map((seat, seatIndex) => (
                        <span 
                          key={seatIndex}
                          className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded font-medium"
                        >
                          {seat}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Available Seat Numbers Detail (for arrays) */}
                {Array.isArray(bus.available_seats_numbers) && bus.available_seats_numbers.length > 8 && (
                  <div className="mt-4 pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-600 mb-2">All available seat numbers:</p>
                    <div className="flex flex-wrap gap-1">
                      {bus.available_seats_numbers.map((seat, seatIndex) => (
                        <span 
                          key={seatIndex}
                          className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded font-medium"
                        >
                          {seat}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}; 